/**
 * Recover the "invited to a document, but the deal invite is still pending" case.
 *
 * ## The failure this replaces
 *
 * A counterparty is invited to a deal AND emailed a "Review document" link. Deal
 * access rights are only created when the INVITATION is accepted
 * (`InvitationManager#grant_deal_access_rights`), so following the document link
 * before accepting hits `DocumentViewPermissionValidatorService#check_deal_access!`
 * → `NoDealAccessException`. The container treated that like any other query
 * failure: an "Unable to fetch the Document" toaster, then a bounce to the deal
 * page — which the same missing access also blocks. The user saw an error for a
 * document they had been legitimately invited to, with no hint of what to do.
 *
 * ## What this does — and explicitly does NOT do
 *
 * It does NOT grant access, and it does not weaken any check. The server remains
 * the only authority: the document stays unreachable until the invitation is
 * accepted through the normal flow, which is what creates the access right.
 *
 * All this does is recognize a RECOVERABLE denial — the user holds a pending
 * invitation to the very deal that owns the document — and route them to the
 * accept-invite screen instead of an error, remembering where they were headed
 * so acceptance lands them on the document they clicked.
 *
 * A denial with no matching pending invitation is NOT recoverable and keeps the
 * existing error behaviour. That distinction is the whole security surface here:
 * we act only on an invitation the server already issued to this user.
 */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import * as queries from '../../../api/queries';

/** Where to send the user after they accept, so the click that got them here completes. */
export const PENDING_DOC_DESTINATION_KEY = 'propdocs:pendingDocumentDestination';

interface PendingInvitationDeal {
  id: string;
  receiverAcceptedInvitationDate?: string | null;
}

interface PendingInvitation {
  token: string;
  status?: string | null;
  deals?: PendingInvitationDeal[] | null;
}

/**
 * True when a GraphQL failure is an access denial rather than a transport or
 * server fault. Only denials are candidates for invitation recovery — a network
 * blip must still surface as an error.
 *
 * Matched on the backend's stable error CODES
 * (`SimpleleaseExceptions::CODE_NO_PROPERTY_ACCESS` = 'no-deal-access',
 * `CODE_NOT_PERMITTED` = 'not-permitted'), with a message fallback because some
 * resolvers surface the denial as a plain `Unhandled server error`.
 */
export function isAccessDenialError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as {
    message?: string;
    graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
  };

  const codes = (anyErr.graphQLErrors ?? [])
    .map((e) => String(e?.extensions?.code ?? e?.extensions?.errorCode ?? ''))
    .filter(Boolean);
  if (codes.some((c) => c === 'no-deal-access' || c === 'not-permitted')) return true;

  const text = [anyErr.message, ...(anyErr.graphQLErrors ?? []).map((e) => e?.message)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return (
    text.includes('permission to view this deal') ||
    text.includes('permission to view this document') ||
    text.includes('no-deal-access') ||
    text.includes('not-permitted')
  );
}

/**
 * Looks up a still-unaccepted invitation covering `dealId`.
 *
 * @returns `findRecoverableInvitationToken` — the invitation token to accept, or
 *          `null` when the denial is not recoverable (no pending invitation for
 *          this deal), in which case the caller keeps its normal error handling.
 */
export function usePendingDealInvitationRecovery(dealId?: string) {
  // The user's OWN invitations — the server already scopes this to `currentUser`,
  // so it discloses nothing they were not sent directly.
  const { data } = useQuery(queries.getUserDealInvitations, {
    fetchPolicy: 'cache-first',
    // A denial is the only consumer; failing this lookup must not break the page.
    onError: () => undefined,
  });

  const findRecoverableInvitationToken = useCallback((): string | null => {
    if (!dealId) return null;
    const invitations: PendingInvitation[] = (data as any)?.currentUser?.dealInvitations ?? [];

    const match = invitations.find((invitation) =>
      (invitation?.deals ?? []).some(
        // Still pending: the deal is listed on the invitation but not yet accepted.
        (deal) => String(deal?.id) === String(dealId) && !deal?.receiverAcceptedInvitationDate
      )
    );

    return match?.token ?? null;
  }, [data, dealId]);

  return { findRecoverableInvitationToken };
}

/** Remember the document the user was trying to open, to resume after acceptance. */
export function rememberDocumentDestination(path: string): void {
  try {
    window.sessionStorage.setItem(PENDING_DOC_DESTINATION_KEY, path);
  } catch {
    /* private mode / storage disabled — recovery still works, just without resume */
  }
}

/** Consume the remembered destination (single-use). */
export function takeDocumentDestination(): string | null {
  try {
    const value = window.sessionStorage.getItem(PENDING_DOC_DESTINATION_KEY);
    if (value) window.sessionStorage.removeItem(PENDING_DOC_DESTINATION_KEY);
    return value;
  } catch {
    return null;
  }
}
